import React, { useRef, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import moment from "moment";
import cx from "classnames";
import { Link } from "react-router-dom";

import global from "../../scss/dhp.scss";
import { userStatusList } from "../Company/CompanyConfig";
import LoadFlagImage from "./LoadFlagImage";
import { Tooltip } from "reactstrap";
import MultiColorList from "./MultiColorList";
import { Icon } from "./icon";
import { setGMTAtSession } from "../../_helpers/utils";
import { parseUserAgent } from "../../_helpers/parseUserAgent ";

let style = Object.assign({}, global);

const dateFormat = {
  "DD-MM-YYYY": "ddd DD MMM",
  "MM-DD-YYYY": "ddd MMM DD",
};

export const DataFormatter = ({
  type,
  slug,
  format,
  data,
  link,
  customMsg,
  showBlank,
  rowNo = 0,
  additionalclass,
  source,
  isRoundOff = true,
  removeSpecialCharacter = false,
  additionalInfo = null,
  showText,
  fallbackSlug,
  isSingleCollection,
}) => {
  const uniqueId = `col-${rowNo}-${type}`;
  const currCell = useRef(null);

  let addclass = "";

  let InlineStyle = {};

  if (source && source === "listview") {
    InlineStyle = { width: "inherit", padding: "0 .5rem 0 1.5rem" };
  }

  const { countries: countryList } = useSelector(state => state?.resources);
  let gmtValue = sessionStorage.getItem("_dh_gmtvalue");

  const userInfo = useSelector(state => state?.auth?.user);

  const [tooltipText, setTooltipText] = useState("");

  const [tooltipOpen, setTooltipOpen] = useState(false);

  const [copied, setCopied] = useState(false);

  const toggle = () => {
    if (currCell.current?.scrollWidth > currCell.current?.offsetWidth) {
      setTooltipText(currCell.current?.children[0]?.innerText);
      setTooltipOpen(!tooltipOpen);
    }
  };

  //To generate default currency format
  function generateDefaultFormat(decimalPlaces, thousandsSeparator, decimalSeparator, numberFormat = "standard") {
    decimalPlaces = parseInt(decimalPlaces);

    const baseNumber = "1234567";
    let integerPart;

    if (numberFormat === "indian") {
      // Indian grouping: 12,34,567
      const last3 = baseNumber.slice(-3);
      const rest = baseNumber.slice(0, -3);
      integerPart = rest.replace(/\B(?=(\d{2})+(?!\d))/g, thousandsSeparator) + thousandsSeparator + last3;
    } else {
      // Standard grouping: 1,234,567
      integerPart = baseNumber.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
    }

    // No decimals
    if (decimalPlaces === 0) return integerPart;

    // Decimal part logic
    const lastDigit = Number(baseNumber.at(-1)); // e.g. 7
    let current = lastDigit;
    let decimalPart = "";

    for (let i = 0; i < decimalPlaces; i++) {
      current = current < 9 ? current + 1 : 9;
      decimalPart += current;
    }

    return `${integerPart}${decimalSeparator}${decimalPart}`;
  }

  // To convert string into separator
  function getSymbolFromName(name) {
    const nameToSymbolMapping = { comma: ",", period: ".", space: " " };
    return nameToSymbolMapping[name];
  }

  // To format Currency
  function formatCurrency(
    value,
    thousandsSeparator = ",",
    decimalSeparator = ".",
    decimalPlaces = 2,
    numberFormat = "standard"
  ) {
    let [integer, decimal] = Number(value)?.toFixed(decimalPlaces).split("."); // Split the number into integer and decimal parts

    if (numberFormat === "indian") {
      const lastThree = integer.slice(-3);
      const rest = integer.slice(0, -3);

      integer = rest
        ? rest.replace(/\B(?=(\d{2})+(?!\d))/g, thousandsSeparator) + thousandsSeparator + lastThree
        : lastThree;
    } else {
      integer = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
    }

    return decimal ? `${integer}${decimalSeparator}${decimal}` : integer;
  }

  // Function to get the properties based on the currency name
  const getCurrencyPropertiesByName = (currencyName, currencies) => {
    if (!currencyName || !currencies) return null; // Return null if either is missing
    const currency = currencies.find(currency => currency.name === currencyName);
    if (currency) {
      return {
        thousand_seperator: getSymbolFromName(currency.thousand_seperator),
        decimal_seperator: getSymbolFromName(currency.decimal_seperator),
        decimal_places: currency.decimal_places,
        symbol: currency.symbol,
        number_format: currency.number_format || "standard",
      };
    }
    return null; // Return null if no matching currency is found
  };

  let result = "";
  let customVal = "";
  let filteredCountry = null;

  // Select date and timeform mat from user information
  const defaultFormats = {
    "date-time": `${dateFormat[userInfo.date_format] || "MMM ddd DD"}, yyyy ${
      userInfo?.time_format?.substring(0, 2) == 24 ? "HH:mm" : "hh:mm A"
    }`,
  };

  const applicableFormat = format == "default" ? defaultFormats[type] : format;

  if (additionalclass) {
    additionalclass.split(" ")?.forEach(cls => (addclass += style[cls] ? `${style[cls]} ` : ""));
    addclass = addclass.trim();
  }

  switch (type) {
    case "text":
      let textValue;
      // Check if the slug has nested key or direct key

      if (slug.includes(".")) {
        const keys = slug.split(".");
        textValue = keys.reduce((acc, key) => acc?.[key], data);
      } else if (removeSpecialCharacter && typeof data?.[slug] === "string" && data?.[slug].includes("-")) {
        textValue = data[slug].replace(/-/g, " ");
      } else {
        textValue = data?.[slug];
      }
      result = (
        <>
          {!link && <span className={style["text-capitalize"]}>{textValue ?? ""}</span>}
          {link && slug === "document_name" && (
            <Link
              to={
                isSingleCollection
                  ? {
                      pathname: `${link}/${data.id}/preview`,
                      state: { fromSingleCollection: true },
                    }
                  : `${link}/${data.id}/preview`
              }>
              {textValue ?? ""}
            </Link>
          )}
          {link && link === "/collection" && <Link to={`${link}/${data._id}`}>{textValue ?? ""}</Link>}
        </>
      );
      break;

    case "description":
      result = <span>{data?.[slug] ?? ""}</span>;
      break;

    case "copy-text":
      let copytextValue;
      // Check if the slug has nested key or direct key
      if (slug.includes(".")) {
        const keys = slug.split(".");
        copytextValue = keys.reduce((acc, key) => acc?.[key], data);
      } else {
        copytextValue = data?.[slug];
      }
      const handleCopy = () => {
        if (copytextValue) {
          navigator.clipboard.writeText(copytextValue);
          setCopied(true);
          setTimeout(() => setCopied(false), 500);
        }
      };
      result = (
        <>
          <span className={style["text-capitalize"]}>
            {copytextValue ?? ""}
            <span onClick={handleCopy} className={cx({ [style["text-success"]]: copied })}>
              <Icon icon="ui-clone" additionalclass="ms-2 font-15 cursor-pointer" />
            </span>
          </span>
        </>
      );
      break;

    case "text-with-percent":
      result = (
        <>
          <span className={style["text-capitalize"]}>{data?.[slug] ?? ""}%</span>
        </>
      );
      break;

    case "text-default":
      result = (
        <>
          <span className={style["text-capitalize"]}>{data?.[slug] ?? ""} </span>
          {data?.["default"] && <span className={style["default-role-text"]}>Default</span>}
        </>
      );
      break;

    case "device-info":
      const info = data?.user_agent ? parseUserAgent(data?.user_agent) : "";
      result = (
        <>
          <span className={style["text-capitalize"]}>{info?.[slug] ?? ""} </span>
        </>
      );
      break;

    case "currency_default_format":
      const thousand_seperator = getSymbolFromName(data?.thousand_seperator);
      const decimal_seperator = getSymbolFromName(data?.decimal_seperator);
      const decimal_place = parseInt(data?.decimal_places);

      result = (
        <>
          {data?.symbol}{" "}
          {generateDefaultFormat(decimal_place, thousand_seperator, decimal_seperator, data?.number_format)}
        </>
      );
      break;

    case "format_currency":
      const val = typeof data?.[slug] === "number" ? data?.[slug] : data?.[slug]?.split(" ").pop();

      const currencyProperties = getCurrencyPropertiesByName(data?.["currency"], additionalInfo);
      result = (
        <>
          {currencyProperties?.symbol}{" "}
          {formatCurrency(
            val,
            currencyProperties?.thousand_seperator,
            currencyProperties?.decimal_seperator,
            currencyProperties?.decimal_places,
            currencyProperties?.number_format
          )}
        </>
      );
      break;

    case "url":
      result = <span>{data?.[slug] ?? ""}</span>;
      break;

    case "email":
      result = (
        <span>
          {slug.includes(".")
            ? (slug.split(".").reduce((acc, key) => acc?.[key], data) ?? data?.[fallbackSlug])
            : (data?.[slug] ?? "")}
        </span>
      );
      break;

    case "currency":
      let number = !isRoundOff ? data?.[slug] || 0 : Math.round(data?.[slug] || 0);
      result = (
        <span>
          {data?.currency ?? ""} {number ?? ""}
        </span>
      );
      break;

    case "link":
      result = (
        <>
          {data?.[slug] && (
            <Link className={style["text-primary"]} to={{ pathname: data?.[slug] }} target="_blank">
              VIEW
            </Link>
          )}
        </>
      );
      break;

    case "user":
      slug?.map(e => {
        const keys = e.split(".");
        let value = data;
        keys.forEach(key => {
          value = value?.[key];
        });
        if (value) {
          result += value + " ";
        }
      });
      result = <span className={style["text-capitalize"]}>{result.trim()}</span>;
      break;

    case "user_info":
      slug.map(e => {
        if (data?.[e]) result += data?.[e] + " ";
      });

      result = (
        <>
          {!link && <span>{result.trim()}</span>}
          {link && <Link to={`${link}/${data.id}`}>{result.trim()}</Link>}
          {/* {data?.["superuser"] && <span className={style["user-role-text"]}>Super Admin</span>} */}
        </>
      );

      break;

    case "date-time":
      let resultDate = "";
      let dateValue;

      // Check if the slug has nested key or direct key
      if (slug.includes(".")) {
        const keys = slug.split(".");
        dateValue = keys.reduce((acc, key) => acc?.[key], data) ?? data?.[fallbackSlug];
      } else {
        dateValue = data?.[slug];
      }

      if (dateValue) {
        if (!gmtValue) gmtValue = setGMTAtSession(userInfo?.time_zone);
        resultDate = moment(dateValue * 1000)
          .utcOffset(gmtValue)
          .format(applicableFormat);
      }

      result = (
        <>
          {dateValue && <span>{resultDate}</span>}
          {!dateValue && ((showBlank && <span></span>) || (!showBlank && <span>Never Logged In</span>))}
        </>
      );
      break;

    case "status":
      result = (
        <span className={cx(style["user-status"], style["text-capitalize"], style[userStatusList[data?.[slug]]])}>
          {" "}
          {userStatusList[data?.[slug]]}
        </span>
      );
      break;

    case "tags":
      result = <span>{data?.[slug]?.join(", ")}</span>;
      break;

    case "dataobj":
      result = (
        <>
          {(data?.action === "Created" || data?.action === "Updated" || data?.action === "Deleted") &&
            data?.record_type === "documents" &&
            data?.[slug]?.link && <Link to={`/${data?.[slug]?.link}/?ref=info`}>{data?.[slug]?.name}</Link>}
          {!data?.[slug]?.link && <span>{data?.[slug]?.name}</span>}
        </>
      );
      break;

    case "boolean":
      customVal = data?.[slug] || false;
      if (customMsg) {
        customVal = customVal === true || customVal === "True" ? customMsg[0] : customMsg[1];
      }
      if (showText) {
        customVal = customVal === true || customVal === "True" ? "Yes" : "No";
      }
      result = <span className={style["text-capitalize"]}>{customVal}</span>;
      break;

    case "multiColor":
      customVal = data?.[slug];

      result = <MultiColorList colors={customVal ?? []} />;
      break;

    case "country":
      // filter country from data
      if (data?.[slug]) {
        filteredCountry = countryList?.find(country => country.iso_code === data[slug]);
        if (filteredCountry) {
          customVal = filteredCountry.name;
          result = (
            <span>
              <LoadFlagImage isoCode={filteredCountry.iso_code} /> {filteredCountry.name}
            </span>
          );
        }
      } else result = <span className={style["text-capitalize"]}></span>;
      break;

    case "custom-font-style":
      let fontFamily = data?.[slug[0]] || false;
      let fontStyle = data?.[slug[1]] || false;
      let fontWeight = data?.[slug[2]] || false;
      result = <span style={{ fontFamily: `"${fontFamily}"`, fontStyle, fontWeight }}>{fontFamily}</span>;
      break;

    case "document_status":
      let status;
      let docInfo = localStorage.getItem("documents");

      // if local storage has data
      if (docInfo) {
        docInfo = JSON.parse(docInfo);

        // if doc id is not present in local storage take value from local storage
        if (docInfo.hasOwnProperty(data.id)) {
          if (docInfo[data.id].publish_id)
            status = { name: "Live", color: "#fcc419" }; // published
          else status = { name: "Draft", color: "#f2f2f2" }; // unpublished
        }
        // if doc id is not present in local storage take value from api
        else {
          // for proposal type doc publisgd/partial signed/ full signed/decliend
          if (data?.proposal?.status) {
            status =
              data?.proposal?.status === "signed"
                ? { name: "Accepted", color: "#40c057" }
                : data?.proposal?.status === "declined"
                  ? { name: "Declined", color: "#b9310c" }
                  : { name: "Live", color: "#fcc419" };
          }
          // for others doc which is published
          else if (data?.publish) status = { name: "Live", color: "#fcc419" };
          // if doc is not published
          else status = { name: "Draft", color: "#f2f2f2" };
        }
      }
      // if local storage has no data : loading time
      else {
        // for proposal type doc publisgd/partial signed/ full signed/decliend
        if (data?.proposal?.status) {
          status =
            data?.proposal?.status === "signed"
              ? { name: "Accepted", color: "#40c057" }
              : data?.proposal?.status === "declined"
                ? { name: "Declined", color: "#b9310c" }
                : { name: "Live", color: "#fcc419" };
        }
        // for others doc which is published
        else if (data?.publish) status = { name: "Live", color: "#fcc419" };
        // if doc is not published
        else {
          status = { name: "Draft", color: "#f2f2f2" };
        }
      }

      result = (
        <div className={cx(style["d-flex"], style["align-items-center"])}>
          <span className={cx(style["color-info"], style["me-2"])} style={{ "background-color": status.color }}></span>
          <span className={style["text-capitalize"]}>{status.name}</span>
        </div>
      );
      break;

    default:
      break;
  }
  let cellData = (
    <div className={cx("text-truncate", addclass)} ref={currCell} style={InlineStyle} id={uniqueId}>
      {result}
      <Tooltip isOpen={tooltipOpen} target={uniqueId} toggle={toggle}>
        {tooltipText}
      </Tooltip>
    </div>
  );
  return cellData;
};

DataFormatter.propTypes = {
  type: PropTypes.string.isRequired,
  slug: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  format: PropTypes.string,
  data: PropTypes.object.isRequired,
  customMsg: PropTypes.array,
  showBlank: PropTypes.bool,
  rowNo: PropTypes.string,
  additionalclass: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  source: PropTypes.string,
};
